/**
 * The shipping quotes a visitor has already been shown, kept for the length of
 * the page session.
 *
 * The product page's flip buttons walk back and forth over the same handful of
 * listings, and every step re-runs the estimate effect: `/api/shipping/estimate`
 * is a session lookup, four Prisma reads and a Shippo (or CJ) call, so flipping
 * one listing forward and back again paid for the same quote twice. The answer
 * does not move in between — same listing, same variant, same buyer, so the same
 * origin, destination and parcel — which makes the second call pure cost, both
 * for the visitor waiting on it and for the route that is already the largest
 * source of application errors in production.
 *
 * Deliberately in-memory and per-tab. A quote is priced against a destination
 * that can change under us — the buyer saves an address, or the edge places the
 * next request in another metro — so it must not outlive the tab, and `TTL_MS`
 * bounds how stale the shown price can get within one.
 *
 * Nothing here touches the network or the DOM; the hook decides what is worth
 * caching and this only remembers it.
 */

/** The part of the estimate the product page renders. */
export interface CachedShippingEstimate {
  /** Dollars, as the buyer sees them. 0 is a real answer ("Free shipping"). */
  shippingEstimate: number;
  /** The cheapest carrier's name, or `USPS` where none was quoted. */
  shippingCarrier: string;
  /** The origin label — a warehouse city, or null when there isn't one. */
  shipsFrom: string | null;
  /** Whether the supplier reported the variant as unavailable. */
  isOutOfStock: boolean;
  /**
   * Dollars of sales tax the route quoted for this destination at this
   * freight. 0 (or absent, on an older answer) means no tax line is drawn.
   */
  taxEstimate?: number;
  /** The receipt label for that tax ("Sales tax", "HST", "GST + PST"). */
  taxLabel?: string;
  /**
   * The effective rate that tax works out to, shown beside the amount. Optional
   * because entries cached before this existed will not carry it, and a missing
   * rate simply draws the label without one.
   */
  taxRatePercent?: number;
  /**
   * Whether that rate is an average rather than the buyer's own rooftop rate,
   * which it is for every quote the built-in table produces.
   */
  taxApproximate?: boolean;
  /**
   * The route crosses a border, so there is no rate: the parties arrange
   * shipping themselves and the page says so instead of showing a number.
   */
  arrangedSeparately?: boolean;
}

/**
 * How long a quote may be re-shown without asking again.
 *
 * Five minutes is long enough to cover flipping through a list and back, and
 * short enough that a buyer who saves an address mid-visit is re-quoted while
 * they are still on the same page rather than at the next reload.
 */
export const TTL_MS = 5 * 60 * 1000;

/**
 * How many listings are remembered at once.
 *
 * A flip session is a walk through one list, so the working set is small; the
 * cap exists so a long browse cannot grow the map without bound. Eviction is
 * least-recently-used: reads re-insert, which moves the entry to the end of the
 * `Map`'s insertion order, and the oldest key is dropped when the cap is hit.
 */
export const MAX_ENTRIES = 50;

interface CacheEntry {
  value: CachedShippingEstimate;
  /** When the quote was stored, as `Date.now()`. */
  storedAt: number;
}

const cache = new Map<string, CacheEntry>();

/**
 * The key a quote is filed under, or null when there is nothing to file.
 *
 * Every input that can change the answer is in the key. The listing and the
 * variant decide the parcel and the origin; the buyer decides the destination,
 * because a signed-in visitor is quoted against their saved address while an
 * anonymous one is quoted against whatever the edge guessed. Keying on the user
 * id alone would serve one buyer's quote to the next one after a sign-in on the
 * same tab.
 */
export function shippingEstimateCacheKey(input: {
  listingId: string | number | null | undefined;
  variantVid?: string | null;
  userId?: string | number | null;
}): string | null {
  const listingId = input.listingId;
  if (listingId === null || listingId === undefined || listingId === '') return null;

  const buyer = input.userId === null || input.userId === undefined || input.userId === ''
    ? 'anon'
    : String(input.userId);

  return `${String(listingId)}|${input.variantVid ?? ''}|${buyer}`;
}

/**
 * The quote stored under this key, or null when there isn't a live one.
 *
 * An expired entry is deleted rather than merely skipped, so a listing the
 * visitor has stopped flipping past does not hold a slot against the cap.
 */
export function readCachedShippingEstimate(
  key: string | null,
  now: number = Date.now(),
): CachedShippingEstimate | null {
  if (!key) return null;

  const entry = cache.get(key);
  if (!entry) return null;

  if (now - entry.storedAt > TTL_MS) {
    cache.delete(key);
    return null;
  }

  // Re-insert so the most recently shown quote is the last to be evicted.
  cache.delete(key);
  cache.set(key, entry);

  return entry.value;
}

/**
 * Remember a quote under this key.
 *
 * The caller passes the values it just rendered; they are copied rather than
 * held by reference so a later state update cannot rewrite what was cached.
 */
export function writeCachedShippingEstimate(
  key: string | null,
  value: CachedShippingEstimate,
  now: number = Date.now(),
): void {
  if (!key) return;

  cache.delete(key);
  cache.set(key, { value: { ...value }, storedAt: now });

  // Map keys iterate in insertion order, so the first is the least recently written.
  while (cache.size > MAX_ENTRIES) {
    const [oldest] = cache.keys();
    cache.delete(oldest);
  }
}

/** Forget everything. For tests, and for a sign-in or sign-out. */
export function clearShippingEstimateCache(): void {
  cache.clear();
}
